/**
 * UUID v7 generator (Web Crypto, no Node APIs).
 *
 * Mirrors `apps/api/src/lib/uuid.ts` byte for byte. Two reasons
 * we duplicate rather than ship a `@avity/uuid` package:
 *   - Zero dependencies, ~60 lines.
 *   - Bundlers tree-shake module-level constants better when the
 *     code is co-located.
 *
 * UUID v7 (RFC 9562) encodes a 48-bit big-endian Unix timestamp
 * in the leading bytes, giving chronologically ordered ids. The
 * sync queue relies on this property: `keys()` from IndexedDB
 * doesn't guarantee ordering, but sorting our id strings as
 * lexicographic ASCII is equivalent to sorting by creation time.
 *
 * Layout (128 bits):
 *   unix_ts_ms : 48 bits
 *   ver        :  4 bits (0b0111 = 7)
 *   rand_a     : 12 bits
 *   var        :  2 bits (0b10)
 *   rand_b     : 62 bits
 */

const HEX = '0123456789abcdef'

export function uuidv7(): string {
  const timestampMillis = BigInt(Date.now())
  const randomBytes = crypto.getRandomValues(new Uint8Array(10))

  const bytes = new Uint8Array(16)
  bytes[0] = Number((timestampMillis >> 40n) & 0xffn)
  bytes[1] = Number((timestampMillis >> 32n) & 0xffn)
  bytes[2] = Number((timestampMillis >> 24n) & 0xffn)
  bytes[3] = Number((timestampMillis >> 16n) & 0xffn)
  bytes[4] = Number((timestampMillis >> 8n) & 0xffn)
  bytes[5] = Number(timestampMillis & 0xffn)

  bytes[6] = 0x70 | (randomBytes[0]! & 0x0f)
  bytes[7] = randomBytes[1]!
  bytes[8] = 0x80 | (randomBytes[2]! & 0x3f)
  bytes[9] = randomBytes[3]!
  bytes[10] = randomBytes[4]!
  bytes[11] = randomBytes[5]!
  bytes[12] = randomBytes[6]!
  bytes[13] = randomBytes[7]!
  bytes[14] = randomBytes[8]!
  bytes[15] = randomBytes[9]!

  return formatUuid(bytes)
}

function formatUuid(bytes: Uint8Array): string {
  let hex = ''
  for (let index = 0; index < 16; index += 1) {
    const byte = bytes[index]!
    hex += HEX[byte >> 4]! + HEX[byte & 0x0f]!
  }
  return (
    hex.slice(0, 8) +
    '-' +
    hex.slice(8, 12) +
    '-' +
    hex.slice(12, 16) +
    '-' +
    hex.slice(16, 20) +
    '-' +
    hex.slice(20, 32)
  )
}
